import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { LOCALES, type Locale, localeFromPath, localeMeta, localePath } from '../i18n/config'

const SITE = 'https://freightratesmcp.com'
const abs = (p: string) => `${SITE}${p === '/' ? '' : p}`

/**
 * Set <title>, meta description, <html lang>, canonical, and reciprocal hreflang alternates after
 * the SPA hydrates — mirroring what prerender.mjs bakes into the static HTML.
 *
 * @param path  the LOGICAL (English) path, e.g. '/' or '/rates/sha-hamburg'. The canonical is the
 *              active locale's variant of this path.
 * @param availableLocales  locales this page actually exists in (default: all). Drives which
 *              hreflang alternates are emitted. If 'en' is absent, x-default falls back to the root.
 */
export function usePageMeta(
  title: string,
  description: string,
  path: string,
  availableLocales: Locale[] = LOCALES.map((l) => l.code)
) {
  const { pathname } = useLocation()
  const locale = localeFromPath(pathname)
  useEffect(() => {
    document.title = title
    document.documentElement.lang = localeMeta(locale).htmlLang
    setMeta('name', 'description', description)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
    setMeta('property', 'og:url', abs(localePath(locale, path)))

    setLink('canonical', abs(localePath(locale, path)))

    // Reset hreflang alternates, then emit one self-referential link per available locale + x-default.
    for (const el of document.querySelectorAll('link[data-i18n="alt"]')) el.remove()
    const avail = LOCALES.filter((l) => availableLocales.includes(l.code))
    for (const l of avail) addAlternate(l.hreflang, abs(localePath(l.code, path)))
    const xDefault = availableLocales.includes('en') ? abs(localePath('en', path)) : abs('/')
    addAlternate('x-default', xDefault)
  }, [title, description, path, locale, availableLocales.join(',')])
}

function setMeta(attr: 'name' | 'property', key: string, value: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = value
}

function setLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!link) {
    link = document.createElement('link')
    link.rel = rel
    document.head.appendChild(link)
  }
  link.href = href
}

function addAlternate(hreflang: string, href: string) {
  const link = document.createElement('link')
  link.rel = 'alternate'
  link.hreflang = hreflang
  link.href = href
  link.setAttribute('data-i18n', 'alt')
  document.head.appendChild(link)
}
